import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { billingErrorMessage } from '../billingRequests';
import type { BillingDetails } from 'api-client/models';
import { useSubscriptionsApi, useUser } from '@/context';
import BillingSummary from './BillingSummary';
import BillingPlans from './BillingPlans';
import { redirectToBilling } from '../billingNavigation';

export default function SubscriptionDetails(): ReactElement {
  const { teamSlug } = useParams<{ teamSlug: string }>();
  const api = useSubscriptionsApi();
  const user = useUser();
  const [billing, setBilling] = useState<BillingDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [reload, setReload] = useState<number>(0);
  const requestVersion = useRef<number>(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setBilling(null);
    setError(null);
    setPending(null);
    if (!teamSlug) {
      setLoading(false);
      return;
    }
    api
      .subscriptionBillingRetrieve({ teamSlug })
      .then((result) => {
        if (active) setBilling(result);
      })
      .catch(async (failure) => {
        const message = await billingErrorMessage(failure);
        if (active) setError(message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      requestVersion.current += 1;
    };
  }, [api, teamSlug, reload]);

  const beginBilling = async (priceId?: string): Promise<void> => {
    if (!teamSlug || pending !== null) return;
    const version = requestVersion.current;
    setPending(priceId || 'portal');
    setError(null);
    try {
      const url = priceId
        ? await api.subscriptionCheckoutCreate({
            teamSlug,
            checkoutRequest: { priceId },
          })
        : await api.subscriptionPortalCreate({ teamSlug });
      if (version === requestVersion.current) redirectToBilling(url);
    } catch (failure) {
      const message = await billingErrorMessage(failure);
      if (version === requestVersion.current) setError(message);
    } finally {
      if (version === requestVersion.current) setPending(null);
    }
  };

  return (
    <Stack spacing={3}>
      <Typography component="h1" variant="h4">
        My Subscription
      </Typography>
      {!teamSlug ? (
        <Alert severity="info">Select a team to view its billing.</Alert>
      ) : loading ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress aria-label="Loading billing" />
          <Typography>Loading billing…</Typography>
        </Stack>
      ) : (
        <>
          {error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={() => setReload((value) => value + 1)}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}
          {billing && !billing.billingEnabled ? (
            <Alert severity="info">
              Platform billing is not enabled during beta. Access does not require an in-app subscription.
            </Alert>
          ) : billing?.subscription ? (
            <BillingSummary
              subscription={billing.subscription}
              invoice={billing.nextInvoice}
              pending={pending !== null}
              onManage={() => void beginBilling()}
              timeZone={user?.timezone || undefined}
            />
          ) : (
            billing && (
              <BillingPlans
                products={billing.products}
                pendingPrice={pending}
                onChoose={(priceId) => void beginBilling(priceId)}
              />
            )
          )}
        </>
      )}
    </Stack>
  );
}
